import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Eye, Share2, Monitor, Tablet, Smartphone, RefreshCw, X, Info } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import WowCXLogo from '@/components/WowCXLogo';

// Use current origin for API calls
const API = `${window.location.origin}/api`;

// WordPress staging preview URL
const WP_STAGING_URL = 'https://wowcx.io/staging';

const SharedDesignPage = () => {
  const { shareToken } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sharedData, setSharedData] = useState(null);
  const [activePage, setActivePage] = useState('Homepage');
  const [previewLoading, setPreviewLoading] = useState(true);
  const [previewTimeout, setPreviewTimeout] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [responsiveViewMode, setResponsiveViewMode] = useState('desktop');

  useEffect(() => {
    fetchSharedDesign();
  }, [shareToken]);

  // Timeout for preview loading (15 seconds)
  useEffect(() => {
    if (previewLoading && sharedData?.preview_token) {
      const timeout = setTimeout(() => {
        if (previewLoading) {
          setPreviewTimeout(true);
          setPreviewLoading(false);
        }
      }, 15000);
      return () => clearTimeout(timeout);
    }
  }, [previewLoading, sharedData?.preview_token]);

  const fetchSharedDesign = async () => {
    try {
      const response = await axios.get(`${API}/shared/${shareToken}`);
      setSharedData(response.data);
      
      // Set initial active page
      if (response.data.design?.elementor_json?.pages?.length > 0) {
        setActivePage(response.data.design.elementor_json.pages[0].page_name);
      }
    } catch (error) {
      console.error('Error fetching shared design:', error);
      setError(error.response?.data?.detail || 'Design not found or link has expired');
    } finally {
      setLoading(false);
    }
  };

  // Get viewport width based on mode
  const getViewportWidth = () => {
    switch (responsiveViewMode) {
      case 'mobile': return '375px';
      case 'tablet': return '768px';
      default: return '100%';
    }
  };

  // Build preview URL with design_id, page, and token
  const getPreviewUrl = () => {
    if (!sharedData?.design?.design_id || !sharedData?.preview_token) return null;
    return `${WP_STAGING_URL}/?design_id=${sharedData.design.design_id}&page=${encodeURIComponent(activePage)}&token=${sharedData.preview_token}`;
  };

  const refreshPreview = () => {
    setPreviewLoading(true);
    setPreviewTimeout(false);
    setPreviewError(null);
    // Force iframe refresh
    const iframe = document.getElementById('shared-preview-iframe');
    if (iframe) {
      iframe.src = iframe.src;
    }
  };

  const handleIframeError = () => {
    setPreviewError('Failed to load preview. The staging site may be unavailable.');
    setPreviewLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-indigo-500 mx-auto mb-4" />
          <p className="text-zinc-400">Loading shared design...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-900 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
            <Share2 className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Link Not Found</h1>
          <p className="text-zinc-400">{error}</p>
        </div>
      </div>
    );
  }

  const { project, design, theme } = sharedData;
  const pages = design?.elementor_json?.pages || [];

  // Get theme colors for display
  const themeColors = design?.elementor_json?.theme_colors || theme?.color_scheme || {};

  return (
    <div className="min-h-screen bg-zinc-900">
      {/* Header */}
      <header className="px-6 md:px-12 py-4 bg-zinc-800 border-b border-zinc-700">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <WowCXLogo size="md" />
            <div className="h-6 w-px bg-zinc-700"></div>
            <div className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-indigo-400" />
              <span className="text-zinc-400 text-sm">Shared Preview</span>
            </div>
            <div className="h-6 w-px bg-zinc-700 hidden md:block"></div>
            <div className="hidden md:block">
              <h1 className="text-lg font-bold text-white">{project.business_name || 'Design Preview'}</h1>
              {project.business_type && (
                <p className="text-xs text-zinc-400">{project.business_type}</p>
              )}
            </div>
          </div>
          
          {theme && (
            <div className="hidden md:flex items-center gap-3">
              <span className="text-zinc-400 text-sm">Theme:</span>
              <span className="text-white font-medium">{theme.name}</span>
              <div className="flex gap-1 ml-2">
                {themeColors.primary && (
                  <div 
                    className="w-5 h-5 rounded-full border border-zinc-600" 
                    style={{ backgroundColor: themeColors.primary }}
                    title="Primary"
                  />
                )}
                {themeColors.accent && (
                  <div 
                    className="w-5 h-5 rounded-full border border-zinc-600" 
                    style={{ backgroundColor: themeColors.accent }}
                    title="Accent"
                  />
                )}
                {themeColors.secondary && (
                  <div 
                    className="w-5 h-5 rounded-full border border-zinc-600" 
                    style={{ backgroundColor: themeColors.secondary }}
                    title="Secondary"
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Page Navigation & View Controls */}
      <div className="px-6 md:px-12 py-4 bg-zinc-800/50">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-4">
          <Tabs value={activePage} onValueChange={setActivePage}>
            <TabsList className="bg-zinc-800 border border-zinc-700">
              {pages.map((page) => (
                <TabsTrigger
                  key={page.page_name}
                  value={page.page_name}
                  className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white text-zinc-400"
                >
                  {page.page_name}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          
          {/* View Controls */}
          <div className="flex items-center gap-2">
            {/* Responsive Mode */}
            <div className="flex gap-1 bg-zinc-800 rounded-lg p-1 border border-zinc-700">
              <button
                onClick={() => setResponsiveViewMode('desktop')}
                className={`p-2 rounded ${responsiveViewMode === 'desktop' ? 'bg-indigo-600 text-white' : 'text-zinc-400 hover:bg-zinc-700'}`}
                title="Desktop view"
              >
                <Monitor className="w-4 h-4" />
              </button>
              <button
                onClick={() => setResponsiveViewMode('tablet')}
                className={`p-2 rounded ${responsiveViewMode === 'tablet' ? 'bg-indigo-600 text-white' : 'text-zinc-400 hover:bg-zinc-700'}`}
                title="Tablet view"
              >
                <Tablet className="w-4 h-4" />
              </button>
              <button
                onClick={() => setResponsiveViewMode('mobile')}
                className={`p-2 rounded ${responsiveViewMode === 'mobile' ? 'bg-indigo-600 text-white' : 'text-zinc-400 hover:bg-zinc-700'}`}
                title="Mobile view"
              >
                <Smartphone className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={refreshPreview}
              className="p-2 rounded bg-zinc-800 border border-zinc-700 text-zinc-400 hover:bg-zinc-700"
              title="Refresh preview"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Design Preview - WordPress Iframe */}
      <div className="px-6 md:px-12 py-8">
        <div className="max-w-7xl mx-auto">
          <div 
            className="bg-zinc-800 rounded-lg shadow-2xl overflow-hidden mx-auto transition-all duration-300 border border-zinc-700"
            style={{ width: getViewportWidth(), maxWidth: '100%' }}
          >
            <div className="relative" style={{ minHeight: '600px' }}>
              {previewLoading && !previewError && !previewTimeout && (
                <div className="absolute inset-0 flex items-center justify-center bg-zinc-800 z-10">
                  <div className="text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-indigo-500 mx-auto mb-3" />
                    <p className="text-sm text-zinc-400">Loading WordPress preview...</p>
                    <p className="text-xs text-zinc-500 mt-1">This may take a few seconds</p>
                  </div>
                </div>
              )}
              
              {/* Error State */}
              {previewError && (
                <div className="flex items-center justify-center h-[600px] bg-red-900/20">
                  <div className="text-center p-8">
                    <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <X className="w-8 h-8 text-red-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">Preview Error</h3>
                    <p className="text-zinc-400 max-w-md mb-4">{previewError}</p>
                    <Button onClick={refreshPreview} className="bg-indigo-600 hover:bg-indigo-700">
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Try Again
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Timeout State */}
              {previewTimeout && !previewError && (
                <div className="flex items-center justify-center h-[600px] bg-amber-900/20">
                  <div className="text-center p-8">
                    <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Info className="w-8 h-8 text-amber-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">Preview Taking Too Long</h3>
                    <p className="text-zinc-400 max-w-md mb-4">
                      The preview is taking longer than expected. Please try again.
                    </p>
                    <Button onClick={refreshPreview} className="bg-indigo-600 hover:bg-indigo-700">
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Retry
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Iframe or Loading Message */}
              {!previewError && !previewTimeout && (
                getPreviewUrl() ? (
                  <iframe
                    id="shared-preview-iframe"
                    src={getPreviewUrl()}
                    className="w-full border-0"
                    style={{ height: '800px' }}
                    onLoad={() => setPreviewLoading(false)}
                    onError={handleIframeError}
                    title="Shared Design Preview"
                  />
                ) : (
                  <div className="flex items-center justify-center h-[600px] bg-zinc-800">
                    <div className="text-center p-8">
                      <Loader2 className="w-8 h-8 animate-spin text-indigo-500 mx-auto mb-3" />
                      <h3 className="text-lg font-semibold text-white mb-2">Preparing Preview</h3>
                      <p className="text-zinc-400 max-w-md">
                        Please wait while we prepare the preview...
                      </p>
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="px-6 md:px-12 py-6 bg-zinc-800 border-t border-zinc-700">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-zinc-500 text-sm">
            This is a shared preview. Design created with{' '}
            <span className="text-indigo-400 font-medium">Wow!CX AI Design Studio</span>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default SharedDesignPage;
